import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { BillingLockedTooltip } from '@/components/billing/BillingLockedTooltip'
import { useCreateStudyEnabled } from '@/hooks/useCreateStudyEnabled'
interface CreateStudyLinkProps {
  href: string
  children: ReactNode
  className?: string
  buttonClassName?: string
  variant?: 'default' | 'cta' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive'
  dataTestId?: string
}

/**
 * Primary CTA to start a new study. Renders a link when the org flag allows
 * creation; otherwise a disabled button with the standard blocked-feature tooltip.
 */
export function CreateStudyLink(props: CreateStudyLinkProps) {
  const { href, children, className, buttonClassName, variant = 'cta', dataTestId } = props
  const createStudyEnabled = useCreateStudyEnabled()
  const { t } = useTranslation('app')
  const blockedMessage = t('blockedFeature.studyCreation')

  if (!createStudyEnabled) {
    return (
      <BillingLockedTooltip message={blockedMessage} className={className}>
        <Button
          variant={variant}
          className={buttonClassName}
          disabled
          data-testid={dataTestId}
        >
          {children}
        </Button>
      </BillingLockedTooltip>
    )
  }

  return (
    <Link href={href} className={className}>
      <Button variant={variant} className={buttonClassName} data-testid={dataTestId}>
        {children}
      </Button>
    </Link>
  )
}
